import React, {useEffect, useState} from "react";
import {AmortizationScheduleLine, FixedAsset, FixedAssetRestControllerApiFactory} from "arteco-api-client-ts";
import {FormContent} from "../../../../components/form/form_inputs/form_buttons";
import {FormInput} from "../../../../components/form/form_inputs/form_input";
import {
    Alert,
    Box,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import {useForm, UseFormProps} from "../../../../components/form/form_model";
import {useApiManager} from "../../../../components/utils/api";
import FormSelect from "../../../../components/form/form_inputs/form_select";
import FormAutocomplete from "../../../../components/form/form_inputs/form_autocomplete";
import {types} from "../../../../components/form/types";
import {fmtAmt} from "../../../../components/utils/numbers";
import {tableContainerSx, tableSx} from "../../../../components/kit";
import {numSx} from "../tax_submission/labels";

export const FixedAssetEditPage = () => {
    const apiManager = useApiManager();
    const ctrl = apiManager.factory(FixedAssetRestControllerApiFactory);

    const [schedule, setSchedule] = useState<AmortizationScheduleLine[]>([]);

    const form = useForm({
        fetcher: (id) => ctrl.getFixedAsset(id),
        updater: (bean) => ctrl.saveFixedAsset(bean),
        remover: (id) => ctrl.deleteOrDismissFixedAsset(id),
        factory: () => ({
            acquisitionDate: new Date().toISOString().split("T")[0],
            amortizationMethod: "LINEAL",
            status: "ACTIVO",
        } as unknown as FixedAsset),
        postLoad: (bean) => {
            if (bean.id) {
                apiManager.execute(ctrl.scheduleFixedAsset(bean.id))
                    .then(s => setSchedule(s as any));
            }
        },
    } as UseFormProps<FixedAsset>);

    const MONTHS = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

    return (
        <FormContent form={form} title={"Activo fijo"}>
            <FormInput field={"name"} label={"Nombre"}/>
            <FormInput field={"description"} label={"Descripción"} rows={2}/>

            <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                    <FormInput field={"acquisitionDate"} label={"Fecha adquisición"} type="date"/>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <FormInput field={"acquisitionValue"} label={"Valor adquisición"} type="number"/>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <FormInput field={"residualValue"} label={"Valor residual"} type="number"/>
                </Grid>
            </Grid>

            <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                    <FormInput field={"usefulLifeMonths"} label={"Vida útil (meses)"} type="number"/>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <FormSelect
                        field={"amortizationMethod"}
                        label={"Método de amortización"}
                        type={types.amortizationMethods}
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <FormSelect
                        field={"status"}
                        label={"Estado"}
                        type={types.fixedAssetStatuses}
                    />
                </Grid>
            </Grid>

            <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                    <FormInput field={"initialAccumulatedAmortization"} label={"Amort. acumulada inicial"} type="number"/>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <FormInput field={"initialAccumulatedDate"} label={"Acumulada hasta"} type="date"/>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="text.secondary" sx={{pt: {xs: 0, sm: 2}}}>
                        La que el activo ya traía de Contasol o de la apertura. Los meses hasta esa fecha no se vuelven a amortizar.
                    </Typography>
                </Grid>
            </Grid>

            <FormAutocomplete
                field={"assetAccount"}
                label={"Cuenta del activo (213...)"}
                type={types.accountingAccounts}
            />
            <FormAutocomplete
                field={"accumulatedAccount"}
                label={"Amortización acumulada (281...)"}
                type={types.accountingAccounts}
            />
            <FormAutocomplete
                field={"dotationAccount"}
                label={"Dotación amortización (681...)"}
                type={types.accountingAccounts}
            />

            {schedule.length > 0 && (
                <Box mt={4}>
                    <Typography variant="subtitle1" fontWeight={700} mb={1.5}>Cuadro de amortización</Typography>
                    <TableContainer sx={tableContainerSx}>
                    <Table size="small" sx={{...tableSx, "& td": {...tableSx["& td"], ...numSx}}}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Año</TableCell>
                                <TableCell>Mes</TableCell>
                                <TableCell align="right">Dotación</TableCell>
                                <TableCell align="right">Amort. acumulada</TableCell>
                                <TableCell align="right">Valor neto contable</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {schedule.map((line, idx) => (
                                <TableRow key={idx}>
                                    <TableCell>{line.year}</TableCell>
                                    <TableCell>{MONTHS[(line.month ?? 1) - 1]}</TableCell>
                                    <TableCell align="right">{fmtAmt(line.dotation)}</TableCell>
                                    <TableCell align="right">{fmtAmt(line.accumulated)}</TableCell>
                                    <TableCell align="right" sx={{fontWeight: 700}}>{fmtAmt(line.netBookValue)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    </TableContainer>
                </Box>
            )}
        </FormContent>
    );
};
